import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../services/api';
import './ClientFormPage.css';

export default function ClientFormPage() {
  const { id } = useParams();
  const isEdit  = Boolean(id);
  const navigate = useNavigate();

  const [form, setForm]     = useState({ name: '', email: '', company: '', phone: '' });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [serverError, setServerError] = useState(null);

  useEffect(() => {
    if (!isEdit) return;
    api.get(`/clients/${id}`).then(data => {
      setForm({
        name:    data.name    ?? '',
        email:   data.email   ?? '',
        company: data.company ?? '',
        phone:   data.phone   ?? '',
        notes:   data.notes   ?? '',
        isActive: data.isActive,
      });
    }).catch(() => navigate('/clients'));
  }, [id, isEdit, navigate]);

  function validate() {
    const e = {};
    if (!form.name.trim())  e.name  = 'Name is required.';
    if (!form.email.trim()) e.email = 'Email is required.';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = 'Enter a valid email.';
    return e;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    const e2 = validate();
    if (Object.keys(e2).length) { setErrors(e2); return; }
    setSaving(true);
    setServerError(null);
    try {
      if (isEdit) {
        await api.put(`/clients/${id}`, { ...form, isActive: form.isActive ?? true });
      } else {
        const res = await api.post('/clients', form);
        navigate(`/clients/${res.id}`);
        return;
      }
      navigate(`/clients/${id}`);
    } catch (err) {
      setServerError(err.message);
    } finally {
      setSaving(false);
    }
  }

  function field(key, label, type = 'text', required = false) {
    return (
      <div className="form-field">
        <label>{label}{required && <span className="required"> *</span>}</label>
        <input
          type={type}
          value={form[key] ?? ''}
          onChange={e => { setForm(f => ({ ...f, [key]: e.target.value })); setErrors(er => ({ ...er, [key]: undefined })); }}
          className={errors[key] ? 'input-error' : ''}
        />
        {errors[key] && <span className="field-error">{errors[key]}</span>}
      </div>
    );
  }

  return (
    <div className="form-page">
      <div className="form-card">
        <h2>{isEdit ? 'Edit Client' : 'Add Client'}</h2>
        {serverError && <div className="error-banner">{serverError}</div>}
        <form onSubmit={handleSubmit} noValidate>
          {field('name',    'Name',    'text',  true)}
          {field('email',   'Email',   'email', true)}
          {field('company', 'Company')}
          {field('phone',   'Phone',   'tel')}
          {isEdit && (
            <div className="form-field">
              <label>Notes</label>
              <textarea
                value={form.notes ?? ''}
                onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                rows={4}
              />
            </div>
          )}
          {isEdit && (
            <div className="form-field checkbox-field">
              <label>
                <input
                  type="checkbox"
                  checked={form.isActive ?? true}
                  onChange={e => setForm(f => ({ ...f, isActive: e.target.checked }))}
                />
                Active client
              </label>
            </div>
          )}
          <div className="form-actions">
            <button type="button" className="btn-secondary" onClick={() => navigate(-1)}>
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={saving}>
              {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Add Client'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
